// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import type { EmailControlSettings } from "@modules/service/reducer/email-controls/types";
import {
  GetEmailControlsSettings,
  UpdateEmailControlsSettings,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type EmailControlsRequest = $Exact<{
  clientId: string,
}>;

type EmailControlsUpdateRequest = $Exact<{
  ...EmailControlsRequest,
  emailControlsSettings: EmailControlSettings,
}>;

export function* onGetEmailControlsSettings({
  payload,
}: Action<EmailControlsRequest>): Generator<*, void, { data: EmailControlSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-email-domains`,
    );

    yield put(actionCreators.GetEmailControlsSettingsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmailControlsSettingsError(e.response));
  }
}

export function* onUpdateEmailControlsSettings({
  payload,
}: Action<EmailControlsUpdateRequest>): Generator<*, void, { data: EmailControlSettings }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-email-domains`,
      payload.emailControlsSettings,
    );

    yield all([
      put(actionCreators.GetEmailControlsSettingsSuccess(response.data)),
      put(actionCreators.UpdateEmailControlsSettingsSuccess()),
      put(actionCreators.ToggleAddEmailDomainModal(false)),
      put(sendSuccessMessage("EMAIL_CONTROL_SETTINGS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmailControlsSettingsError(e.response));
  }
}

export function* EmailControlsSagas(): Generator<*, void, void> {
  yield all([takeEvery(GetEmailControlsSettings, onGetEmailControlsSettings)]);
  yield all([takeEvery(UpdateEmailControlsSettings, onUpdateEmailControlsSettings)]);
}

export default EmailControlsSagas;
